import {
  APIError,
  type NotificationUnreadCountInput,
  type NotificationUnreadCountOutput,
  isDefined,
  messageFromError,
} from '@terros/common'
import { useIsMutating, useQuery } from '@tanstack/react-query'
import { type GetHookResponse } from '../hooks'
import { useServerRequest } from '../api'

export type NotificationCountResponse = GetHookResponse<NotificationUnreadCountOutput> & {
  available: boolean
  unavailable: boolean
}

// This endpoint uses the company-only inbox gate, unlike /feature/check's user/team rules.
export function useNotificationUnreadCount(): NotificationCountResponse {
  const request = useServerRequest<NotificationUnreadCountInput, NotificationUnreadCountOutput>('/notification/count')
  const pending = useIsMutating({ mutationKey: ['notification-center', 'read-update'] }) > 0
  const query = useQuery({
    queryKey: ['notification-center', 'count'],
    queryFn: () => request({}),
    enabled: (query) => !pending && !isNotificationUnauthorized(query.state.error),
    refetchInterval: 45000,
    refetchOnWindowFocus: true,
    retry: false,
  })
  const unavailable = isNotificationUnauthorized(query.error)
  return {
    ...query,
    lastUpdatedAt: query.dataUpdatedAt,
    error: messageFromError(query.error),
    available: isDefined(query.data) && !unavailable,
    unavailable,
  }
}

export function isNotificationUnauthorized(error: unknown): boolean {
  return error instanceof APIError && error.error.error === 'NotAuthorized'
}
